'use client';

/**
 * Global Error Page
 * Fun, branded error message with animation and navigation.
 * @module ErrorHandler
 */
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import ROUTES from '@/lib/constants/routeConfig';
import { useI18n } from '@/locales/client';

const ErrorHandler = () => {
  const router = useRouter();

  const t = useI18n();
  return (
    <div className='flex min-h-dvh items-center justify-center bg-[#F9FAFB] px-4 py-8 dark:bg-background'>
      <div className='flex w-full max-w-md flex-col items-center rounded-xl border border-gray-200 bg-white/90 p-8 shadow-lg dark:border-gray-700 dark:bg-gray-900/80'>
        {/* Animated logo */}
        <div className='mb-6 flex flex-col items-center'>
          <div className='animate-spin-slow relative mb-2 h-20 w-20'>
            <Image
              src='/images/JAQLNLogo.png'
              alt='Jaqln Logo'
              fill
              priority
              sizes='80px'
              style={{ objectFit: 'cover' }}
            />
          </div>
          <div className='flex h-4 items-end space-x-1.5'>
            <span className='h-2 w-2 animate-bounce rounded-full bg-purple-600 dark:bg-purple-400' />
            <span
              className='h-2 w-2 animate-bounce rounded-full bg-purple-600 dark:bg-purple-400'
              style={{ animationDelay: '0.2s' }}
            />
            <span
              className='h-2 w-2 animate-bounce rounded-full bg-purple-600 dark:bg-purple-400'
              style={{ animationDelay: '0.4s' }}
            />
          </div>
        </div>
        {/* Error Message */}
        <h1 className='mb-2 text-center text-2xl font-bold text-gray-900 dark:text-white'>
          {t('error.message2')}
        </h1>
        <p className='mb-6 text-center text-gray-600 dark:text-gray-300'>
          {t('error.message')}
          <br />
          {t('error.errorOptions')}
        </p>
        {/* Navigation button */}
        <button
          className='w-full rounded-md bg-[#9147ff] px-4 py-2 font-semibold text-white shadow transition-colors hover:bg-[#7a36d4]'
          onClick={() => router.push(ROUTES.HOME)}
          aria-label='Go to homepage'
        >
          {t('error.goHome')}
        </button>
      </div>
      <style jsx global>{`
        @keyframes spin-slow {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .animate-spin-slow {
          animation: spin-slow 2.5s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default function GlobalError({
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  return <ErrorHandler />;
}
